import { Cancel } from "@mui/icons-material";
import { useMemo, useState, useTransition, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { AccessControl, MuiButton } from "@ryde/controls";
import {
  useCrudEditContext,
  useDynamicFormsListActions,
  useFeedbacksEditDrawerLogic,
  useModalConfirm,
} from "@ryde/redux";
import {
  ActiveFeedbacksStatusSet,
  ConfirmResult,
  errors,
  FeedbackApi,
  FeedbackStatusEnum,
  Log,
  normalizeErrors,
  SINGLE_KEY,
  transformApiCrudErrorForToast,
  useHasPermissionsCheck,
  useSystemParams,
  type IApiError,
  type IExtendableDto,
  type RydeUiUnit,
} from "@ryde/shared";

interface IFeedbackExpand {
  entity: RydeUiUnit;
  onSaveClick?: () => void;
}

export const FeedbackExpand: FC<IFeedbackExpand> = ({
  entity,
  onSaveClick,
}) => {
  const {
    watch,
    formState: { dirtyFields },
  } = useFormContext();
  // Use the drawer's CRUD instance: a second useCrudEdit here clears the shared dto and loading
  // flag whenever the drawer's loader unmounts this footer, so the drawer refetches in a loop.
  const {
    data: { fetchDto, save },
  } = useCrudEditContext<IExtendableDto>();

  useFeedbacksEditDrawerLogic();
  const {
    sendFeedbackToSupplierOnCreation,
    sendFeedbackToAdminOnSupplierResponse,
  } = useSystemParams();

  const [isLoading, setIsLoading] = useState(false);
  const { confirmDialogResult } = useModalConfirm();
  const { replaceTableRow } = useDynamicFormsListActions(entity);
  const [, startTransition] = useTransition();
  const { t } = useTranslation();
  const feedbackId = watch("id");
  const feedbackStatus = watch("status");
  const supplierName = watch("metadata.supplierName");
  const supplierResponse = watch("supplierResponse");
  const isHasDirtyFields = Object.values(dirtyFields)?.length > 0;
  const entityTranslatedName = t(`feedback.${SINGLE_KEY}`);
  const isFeedbackActive = ActiveFeedbacksStatusSet.has(feedbackStatus);

  const { hasPermissionsCheck } = useHasPermissionsCheck();

  const isValidSendToSupplier = useMemo(() => {
    return Boolean(
      feedbackId && supplierName && feedbackStatus === FeedbackStatusEnum.New,
    );
  }, [feedbackId, feedbackStatus, supplierName]);

  const isValidSendSupplierResponse = useMemo(() => {
    return Boolean(
      feedbackId &&
      supplierResponse?.length &&
      feedbackStatus === FeedbackStatusEnum.WaitingForSupplier,
    );
  }, [feedbackId, feedbackStatus, supplierResponse?.length]);

  const isCancelButtonDisplayed = useMemo(() => {
    return (
      Boolean(feedbackId) &&
      isFeedbackActive &&
      (hasPermissionsCheck({ entity, action: "act_as_admin" }) ||
        (hasPermissionsCheck({ entity, action: "act_as_operator" }) &&
          feedbackStatus !== FeedbackStatusEnum.WaitingForAdmin))
    );
  }, [
    entity,
    feedbackId,
    feedbackStatus,
    hasPermissionsCheck,
    isFeedbackActive,
  ]);

  const updateRowData = useEvent((updatedFeedback: IExtendableDto): void => {
    if (updatedFeedback.id) {
      startTransition(() => {
        fetchDto?.(updatedFeedback.id);
        replaceTableRow(updatedFeedback);
      });
    }
  });

  const sendToSupplierClick = useEvent(async () => {
    if (isHasDirtyFields && onSaveClick) {
      onSaveClick();
    }

    try {
      setIsLoading(true);
      const response = await FeedbackApi.sendToSupplier(feedbackId);

      if (response?.success && response.data) {
        Log.successAndToast(t("general.MESSAGE_SENT_SUCCESSFULLY"));
        updateRowData(response.data);
      } else {
        Log.errorAndToast(t(errors.feedback.failedToSendFeedbackToSupplier));
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      Log.errorAndToast(
        transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
      );
    } finally {
      setIsLoading(false);
    }
  });

  const sendSupplierResponseClick = useEvent(async () => {
    if (isHasDirtyFields && onSaveClick) {
      onSaveClick();
    }

    try {
      setIsLoading(true);
      const response = await FeedbackApi.sendSupplierResponse(feedbackId);

      if (response?.success && response.data) {
        Log.successAndToast(t("general.MESSAGE_SENT_SUCCESSFULLY"));
        updateRowData(response.data);
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      Log.errorAndToast(
        transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
      );
    } finally {
      setIsLoading(false);
    }
  });

  const onCancelFeedbackClick = useEvent(async () => {
    if (!feedbackId) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.ARE_YOU_SURE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      setIsLoading(true);

      try {
        const response = await save?.({
          status: FeedbackStatusEnum.Canceled,
        });

        if (response?.success && response.data) {
          Log.successAndToast(
            t("general.ENTITY_SUCCESSFULLY_UPDATED", {
              entity: entityTranslatedName,
            }),
          );

          updateRowData(response.data);
        }
      } finally {
        setIsLoading(false);
      }
    }
  });

  return (
    <>
      {!sendFeedbackToSupplierOnCreation && (
        <AccessControl entity={entity || "unknown"} action="act_as_admin">
          <MuiButton
            e2eData={`feedback_send_to_supplier_button`}
            muiStyle={"secondary"}
            onClick={sendToSupplierClick}
            disabled={!isValidSendToSupplier}
            loading={isLoading}
          >
            {t(`general.SEND_TO_SUPPLIER`)}
          </MuiButton>
        </AccessControl>
      )}

      {!sendFeedbackToAdminOnSupplierResponse && (
        <AccessControl entity={entity || "unknown"} action="act_as_supplier">
          <MuiButton
            e2eData={`feedback_send_to_admin_button`}
            muiStyle={"secondary"}
            onClick={sendSupplierResponseClick}
            disabled={!isValidSendSupplierResponse}
            loading={isLoading}
          >
            {t(`general.SEND_TO_ADMIN`)}
          </MuiButton>
        </AccessControl>
      )}

      {isCancelButtonDisplayed && (
        <MuiButton
          onClick={onCancelFeedbackClick}
          e2eData={`feedback_cancel_button`}
          muiStyle={"error"}
          disabled={isLoading}
          loading={isLoading}
        >
          <Cancel />
          {`${t(`general.CANCEL`)} ${entityTranslatedName}`}
        </MuiButton>
      )}
    </>
  );
};
